import React, { useState, useEffect, useMemo, useRef } from "react";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Stack,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";

// Styles
import "../OpdStepper.css";

export const vitalsInitialValues = {
  weight: "",
  height: "",
  bmi: "",
  temperature: "",
  abdominalCircumference: "",
  hipCircumference: "",
  waistHipRatio: "",
  pulseRate: "",
  respiratoryRate: "",
  bloodPressure: "",
  randomBloodSugar: "",
  haemoglobin: "",
  clinicalFindings: "",
};

// Blood pressure is entered as two segments — 2-3 digits of systolic and 2 of
// diastolic — and persisted as a single "systolic/diastolic" string.
const BP_SYSTOLIC_DIGITS = 3;
const BP_DIASTOLIC_DIGITS = 2;

// A value that carries no digits (untouched field, or just the bare mask) is
// treated as "not recorded" rather than as an invalid entry.
export const normalizeBloodPressure = (value) => {
  const text = String(value ?? "").trim();
  return /\d/.test(text) ? text : "";
};

// Coerces a stored value (older records were saved as "120 / 80") into the mask.
export const toBloodPressureMask = (value) => {
  const text = normalizeBloodPressure(value);
  if (!text) return "";
  const [left = "", right = ""] = text.split("/");
  const systolic = left.replace(/\D/g, "").slice(0, BP_SYSTOLIC_DIGITS);
  const diastolic = right.replace(/\D/g, "").slice(0, BP_DIASTOLIC_DIGITS);
  return `${systolic}/${diastolic}`;
};

export const vitalsValidationSchema = Yup.object().shape({
  weight: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(1, "Weight must be positive")
    .max(500, "Weight exceeds standard limits"),
  height: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(30, "Height must be at least 30 cm")
    .max(300, "Height exceeds standard limits"),
  bmi: Yup.mixed().nullable(),
  temperature: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(86, "Temperature must be at least 86°F")
    .max(113, "Temperature exceeds standard limits"),
  abdominalCircumference: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(10, "Circumference must be at least 10 cm")
    .max(250, "Circumference exceeds standard limits"),
  hipCircumference: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(10, "Circumference must be at least 10 cm")
    .max(250, "Circumference exceeds standard limits"),
  waistHipRatio: Yup.mixed().nullable(),
  pulseRate: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(30, "Pulse must be at least 30 bpm")
    .max(250, "Pulse exceeds standard limits"),
  respiratoryRate: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(5, "Rate must be at least 5 /min")
    .max(80, "Rate exceeds standard limits"),
  bloodPressure: Yup.string()
    .nullable()
    .transform((_value, originalValue) => normalizeBloodPressure(originalValue) || null)
    .matches(/^\d{2,3}\/\d{2}$/, "Use systolic/diastolic, e.g. 120/80 or 45/30"),
  randomBloodSugar: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(10, "Must be at least 10 mg/dL")
    .max(800, "Cannot exceed 800 mg/dL"),
  haemoglobin: Yup.number()
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? null : value))
    .min(1, "Must be at least 1 g/dL")
    .max(25, "Cannot exceed 25 g/dL"),
  clinicalFindings: Yup.string().nullable(),
});

function AutoCalculateVitals() {
  const { values, setFieldValue } = useFormikContext();

  // 1. Auto-calculate BMI
  useEffect(() => {
    const w = parseFloat(values.weight);
    const h = parseFloat(values.height);
    if (w > 0 && h > 0) {
      const bmiVal = (w / ((h / 100) * (h / 100))).toFixed(1);
      if (values.bmi !== bmiVal) {
        setFieldValue("bmi", bmiVal);
      }
    } else {
      if (values.bmi !== "") {
        setFieldValue("bmi", "");
      }
    }
  }, [values.weight, values.height, values.bmi, setFieldValue]);

  // 2. Auto-calculate Waist to Hip Ratio
  useEffect(() => {
    const abd = parseFloat(values.abdominalCircumference);
    const hip = parseFloat(values.hipCircumference);
    if (abd > 0 && hip > 0) {
      const ratio = (abd / hip).toFixed(2);
      if (values.waistHipRatio !== ratio) {
        setFieldValue("waistHipRatio", ratio);
      }
    } else {
      if (values.waistHipRatio !== "") {
        setFieldValue("waistHipRatio", "");
      }
    }
  }, [values.abdominalCircumference, values.hipCircumference, values.waistHipRatio, setFieldValue]);

  return null;
}

// Systolic and diastolic are captured in two separate inputs, but stored in the
// single "bloodPressure" form field as "systolic/diastolic".
function BloodPressureField() {
  const { values, errors, setFieldValue, setFieldTouched } = useFormikContext();
  const systolicRef = useRef(null);
  const diastolicRef = useRef(null);

  const [systolic = "", diastolic = ""] = String(values.bloodPressure || "").split("/");
  const hasError = Boolean(errors.bloodPressure);

  const commit = (nextSystolic, nextDiastolic) => {
    const combined = nextSystolic || nextDiastolic ? `${nextSystolic}/${nextDiastolic}` : "";
    setFieldValue("bloodPressure", combined);
  };

  const handleSystolicChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, BP_SYSTOLIC_DIGITS);
    commit(digits, diastolic);
    // A full 3-digit systolic can only be followed by the diastolic, so jump
    // across. Shorter readings (infants) move on via "/", Tab or a click.
    if (digits.length === BP_SYSTOLIC_DIGITS) diastolicRef.current?.focus();
  };

  const handleDiastolicChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, BP_DIASTOLIC_DIGITS);
    commit(systolic, digits);
  };

  const handleSystolicKeyDown = (event) => {
    if (event.key === "/" || (event.key === "ArrowRight" && !event.shiftKey)) {
      const input = event.target;
      if (event.key === "ArrowRight" && input.selectionStart !== input.value.length) return;
      event.preventDefault();
      diastolicRef.current?.focus();
    }
  };

  const handleDiastolicKeyDown = (event) => {
    const atStart = event.target.selectionStart === 0 && event.target.selectionEnd === 0;
    if ((event.key === "Backspace" && !diastolic) || (event.key === "ArrowLeft" && atStart)) {
      event.preventDefault();
      systolicRef.current?.focus();
    }
  };

  const markTouched = () => setFieldTouched("bloodPressure", true);

  const segmentProps = {
    size: "small",
    className: "opd-input-field",
    error: hasError,
    inputMode: "numeric",
    onBlur: markTouched,
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <TextField
          {...segmentProps}
          placeholder="120"
          value={systolic}
          sx={{ width: 76, flexShrink: 0 }}
          inputRef={systolicRef}
          onChange={handleSystolicChange}
          onKeyDown={handleSystolicKeyDown}
          inputProps={{ maxLength: BP_SYSTOLIC_DIGITS, "aria-label": "Systolic blood pressure" }}
        />
        <Typography sx={{ color: "var(--opd-label)", fontWeight: 600 }}>/</Typography>
        <TextField
          {...segmentProps}
          placeholder="80"
          value={diastolic}
          sx={{ flex: 1, minWidth: 0 }}
          inputRef={diastolicRef}
          onChange={handleDiastolicChange}
          onKeyDown={handleDiastolicKeyDown}
          inputProps={{ maxLength: BP_DIASTOLIC_DIGITS, "aria-label": "Diastolic blood pressure" }}
          InputProps={{
            endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
          }}
        />
      </Stack>
      <FormHelperText error={hasError}>{errors.bloodPressure || " "}</FormHelperText>
    </>
  );
}

export default function Step3VitalsExamination({
  nextStep,
  prevStep,
  initialValues,
  submitLabel = "Next",
  showActions = true,
}) {
  const [expanded, setExpanded] = useState({
    vitals: true,
    findings: true,
  });

  const [fetchedData, setFetchedData] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // Load existing step 3 data if resuming visit
  useEffect(() => {
    let active = true;
    const displayId = localStorage.getItem("currentOpdDisplayId") || "";
    if (displayId) {
      const fetchStep3Data = async () => {
        try {
          const res = await eopdAPI.getOpdStepData(displayId, 3);
          if (active && res && res.success && res.data) {
            const v = res.data.vitals || res.data;
            const toStr = (val) => (val !== null && val !== undefined ? String(val) : "");
            setFetchedData({
              weight: toStr(v.weight),
              height: toStr(v.height),
              bmi: toStr(v.bmi),
              temperature: toStr(v.temperature ?? v.temp),
              abdominalCircumference: toStr(v.abdominal_circumference ?? v.abd_circum),
              hipCircumference: toStr(v.hip_circumference ?? v.hip_circum),
              waistHipRatio: toStr(v.waist_to_hip_ratio ?? v.waist_hip_ratio),
              pulseRate: toStr(v.pulse_rate),
              respiratoryRate: toStr(v.respiratory_rate ?? v.resp_rate),
              bloodPressure: toBloodPressureMask(v.blood_pressure ?? v.bp ?? ""),
              randomBloodSugar: toStr(v.random_blood_sugar ?? v.rand_blood_sugar),
              haemoglobin: toStr(v.haemoglobin),
              clinicalFindings: v.findings || "",
            });
          }
        } catch (err) {
          console.error("Error loading step 3 data:", err);
        }
      };
      fetchStep3Data();
    }
    return () => {
      active = false;
    };
  }, []);

  const initialFormValues = useMemo(() => {
    const merged = { ...vitalsInitialValues, ...(initialValues || {}), ...(fetchedData || {}) };
    return { ...merged, bloodPressure: toBloodPressureMask(merged.bloodPressure) };
  }, [initialValues, fetchedData]);

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={vitalsValidationSchema}
      enableReinitialize
      onSubmit={async (values, helpers) => {
        try {
          const toNum = (v) => (String(v ?? "").trim() === "" ? null : Number(v));

          const displayId = localStorage.getItem("currentOpdDisplayId") || "";
          const payload = {
            display_id: displayId,
            weight: toNum(values.weight),
            height: toNum(values.height),
            bmi: toNum(values.bmi),
            temperature: toNum(values.temperature),
            abdominal_circumference: toNum(values.abdominalCircumference),
            hip_circumference: toNum(values.hipCircumference),
            waist_to_hip_ratio: toNum(values.waistHipRatio),
            pulse_rate: toNum(values.pulseRate),
            respiratory_rate: toNum(values.respiratoryRate),
            blood_pressure: normalizeBloodPressure(values.bloodPressure) || null,
            random_blood_sugar: toNum(values.randomBloodSugar),
            haemoglobin: toNum(values.haemoglobin),
            findings: values.clinicalFindings?.trim() || null,
          };
          const response = await eopdAPI.saveOpdStep(3, payload);
          if (response && response.success) {
            toast.success(response.message || "Vitals saved successfully!");
            nextStep();
          } else {
            toast.error(response.message || "Failed to save vitals.");
          }
        } catch (error) {
          console.error(error);
          const errMsg = error.response?.data?.message || error.message || "Failed to save vitals.";
          toast.error(errMsg);
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, handleChange, handleBlur, isSubmitting }) => (
        <Form noValidate>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isSubmitting}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <AutoCalculateVitals />

          <Typography className="opd-step-title">
            Vitals &amp; Clinical Examination
          </Typography>

          {/* 1. Vitals Accordion */}
          <Accordion
            expanded={expanded.vitals}
            onChange={handleAccordionChange("vitals")}
            className={`opd-accordion ${expanded.vitals ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">Vitals</Typography>
                {/* <Typography className="opd-accordion-subtitle">
                  Record physical vital signs. Warns if values are out of range.
                </Typography> */}
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Weight</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 72.5"
                      name="weight"
                      value={values.weight}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.weight)}
                      helperText={errors.weight || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Height</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 175"
                      name="height"
                      value={values.height}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.height)}
                      helperText={errors.height || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">BMI</Typography>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      placeholder="0.0"
                      name="bmi"
                      value={values.bmi}
                      className="opd-input-field"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kg/m²</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Temperature</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 98.6"
                      name="temperature"
                      value={values.temperature}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.temperature)}
                      helperText={errors.temperature || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">°F</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Abdominal Circumference</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 88"
                      name="abdominalCircumference"
                      value={values.abdominalCircumference}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.abdominalCircumference)}
                      helperText={errors.abdominalCircumference || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Hip Circumference</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 96"
                      name="hipCircumference"
                      value={values.hipCircumference}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.hipCircumference)}
                      helperText={errors.hipCircumference || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Waist to Hip Ratio</Typography>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      placeholder="0.0"
                      name="waistHipRatio"
                      value={values.waistHipRatio}
                      className="opd-input-field"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Pulse Rate</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 72"
                      name="pulseRate"
                      value={values.pulseRate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.pulseRate)}
                      helperText={errors.pulseRate || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Respiratory Rate</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 16"
                      name="respiratoryRate"
                      value={values.respiratoryRate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.respiratoryRate)}
                      helperText={errors.respiratoryRate || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">/min</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Blood Pressure</Typography>
                    <BloodPressureField />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Random Blood Sugar</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 110"
                      name="randomBloodSugar"
                      value={values.randomBloodSugar}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.randomBloodSugar)}
                      helperText={errors.randomBloodSugar || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">mg/dL</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Haemoglobin</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. 14.2"
                      name="haemoglobin"
                      value={values.haemoglobin}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.haemoglobin)}
                      helperText={errors.haemoglobin || " "}
                      className="opd-input-field"
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">g/dL</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* 2. Clinical Findings Accordion */}
          <Accordion
            expanded={expanded.findings}
            onChange={handleAccordionChange("findings")}
            className={`opd-accordion ${expanded.findings ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">Clinical Examination Findings</Typography>
                <Typography className="opd-accordion-subtitle">
                  General physical exam notes and systemic observations.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Box className="opd-input-group">
                <Typography className="opd-field-label">Findings</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Enter systemic observations, general physical exam notes, pallor, icterus, clubbing, cyanosis, lymphadenopathy, or edema details..."
                  name="clinicalFindings"
                  value={values.clinicalFindings}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.clinicalFindings)}
                  helperText={errors.clinicalFindings || `${(values.clinicalFindings || "").length}/1000`}
                  className="opd-input-field"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {showActions && (
            <Stack
              direction="row"
              justifyContent={prevStep ? "space-between" : "flex-end"}
              alignItems="center"
              sx={{ mt: 3 }}
            >
              {prevStep && (
                <Button
                  type="button"
                  variant="outlined"
                  className="opd-back-btn"
                  onClick={prevStep}
                >
                  Back
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={isSubmitting}
                className="opd-next-btn"
              >
                {submitLabel}
              </Button>
            </Stack>
          )}
        </Form>
      )}
    </Formik>
  );
}
